/**
 * Google Analytics 4 Tracker
 *
 * Provides GA4 event tracking integration for WooAI Chatbot Pro.
 * Automatically detects existing GA4 installation and tracks chatbot events.
 *
 * @package WooAIChatbot
 * @since 0.2.0
 */
/**
 * Internal event to GA4 event name mapping
 */
const EVENT_NAME_MAP = {
    // Engagement events
    conversation_start: 'engage_widget',
    widget_open: 'widget_open',
    widget_close: 'widget_close',
    message_sent: 'chat_message',
    message_received: 'chat_response',
    // Product events
    product_click: 'select_item',
    product_view: 'view_item',
    products_viewed: 'view_item_list',
    // Ecommerce events (GA4 standard names)
    add_to_cart: 'add_to_cart',
    remove_from_cart: 'remove_from_cart',
    checkout_start: 'begin_checkout',
    purchase: 'purchase',
    // Session events
    session_start: 'session_start',
    session_end: 'session_end',
    // Topic events
    topic_detected: 'topic_detected',
    playbook_started: 'playbook_started',
    playbook_completed: 'playbook_completed',
    // Error events
    error: 'exception',
};
/**
 * GA4 Tracker Class
 *
 * Singleton class for tracking events to Google Analytics 4.
 * Automatically detects if GA4 is installed on the page.
 */
class GA4Tracker {
    constructor() {
        Object.defineProperty(this, "measurementId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "debugMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
    }
    /**
     * Initialize the tracker with a measurement ID
     *
     * @param measurementId - GA4 Measurement ID (e.g., G-XXXXXXXXXX)
     * @param options - Optional configuration
     */
    init(measurementId, options) {
        if (!measurementId || typeof measurementId !== 'string') {
            console.warn('[GA4Tracker] Invalid measurement ID provided');
            return;
        }
        // Validate measurement ID format (G-XXXXXXXXXX)
        if (!/^G-[A-Z0-9]+$/.test(measurementId)) {
            console.warn('[GA4Tracker] Measurement ID should be in format G-XXXXXXXXXX');
        }
        this.measurementId = measurementId;
        this.debugMode = options?.debug ?? false;
        this.isInitialized = true;
        // Debug logging removed for production
    }
    /**
     * Check if GA4 is available on the page
     *
     * @returns True if gtag function exists
     */
    isAvailable() {
        return typeof window.gtag === 'function';
    }
    /**
     * Check if tracker has been initialized
     *
     * @returns True if init() has been called with valid ID
     */
    isReady() {
        return this.isInitialized && this.measurementId !== null;
    }
    /**
     * Track an event to GA4
     *
     * @param eventName - Internal event name (will be mapped to GA4 format)
     * @param params - Optional event parameters
     */
    track(eventName, params) {
        if (!this.isAvailable()) {
            return;
        }
        if (!eventName || typeof eventName !== 'string') {
            console.warn('[GA4Tracker] Invalid event name');
            return;
        }
        // Map internal event name to GA4 format
        const ga4EventName = this.mapEventName(eventName);
        // Build event parameters
        const eventParams = {
            event_category: 'wooai_chatbot',
            ...params,
        };
        // Add measurement ID if configured
        if (this.measurementId) {
            eventParams.send_to = this.measurementId;
        }
        // Add debug parameter if in debug mode
        if (this.debugMode) {
            eventParams.debug_mode = true;
        }
        try {
            window.gtag?.('event', ga4EventName, eventParams);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[GA4Tracker] Error tracking event:', error);
            }
        }
    }
    /**
     * Track an ecommerce event with items
     *
     * @param eventName - Ecommerce event name
     * @param items - Array of items
     * @param params - Additional parameters
     */
    trackEcommerce(eventName, items, params) {
        const eventParams = {
            ...params,
            items,
            currency: params?.currency ?? 'USD',
        };
        // Calculate total value if not provided
        if (typeof eventParams.value !== 'number' && items.length > 0) {
            eventParams.value = items.reduce((total, item) => {
                const itemValue = (item.price ?? 0) * (item.quantity ?? 1);
                return total + itemValue;
            }, 0);
        }
        this.track(eventName, eventParams);
    }
    /**
     * Track a page view
     *
     * @param pageTitle - Optional page title
     * @param pageLocation - Optional page URL
     */
    trackPageView(pageTitle, pageLocation) {
        if (!this.isAvailable()) {
            return;
        }
        const params = {};
        if (pageTitle) {
            params.page_title = pageTitle;
        }
        if (pageLocation) {
            params.page_location = pageLocation;
        }
        if (this.measurementId) {
            params.send_to = this.measurementId;
        }
        try {
            window.gtag?.('event', 'page_view', params);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[GA4Tracker] Error tracking page view:', error);
            }
        }
    }
    /**
     * Set user properties
     *
     * @param properties - User properties to set
     */
    setUserProperties(properties) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            window.gtag?.('set', 'user_properties', properties);
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[GA4Tracker] Error setting user properties:', error);
            }
        }
    }
    /**
     * Set user ID for cross-device tracking
     *
     * @param userId - User identifier
     */
    setUserId(userId) {
        if (!this.isAvailable() || !userId) {
            return;
        }
        try {
            window.gtag?.('set', { user_id: String(userId) });
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[GA4Tracker] Error setting user ID:', error);
            }
        }
    }
    /**
     * Map internal event name to GA4 format
     *
     * @param event - Internal event name
     * @returns GA4 event name
     */
    mapEventName(event) {
        // Check if we have a mapping
        if (EVENT_NAME_MAP[event]) {
            return EVENT_NAME_MAP[event];
        }
        // Convert to snake_case if not already
        const snakeCase = event
            .replace(/([a-z])([A-Z])/g, '$1_$2')
            .replace(/[\s-]+/g, '_')
            .toLowerCase();
        // Prefix with wooai_ for custom events
        if (!snakeCase.startsWith('wooai_')) {
            return `wooai_${snakeCase}`;
        }
        return snakeCase;
    }
    /**
     * Enable debug mode
     */
    enableDebug() {
        this.debugMode = true;
    }
    /**
     * Disable debug mode
     */
    disableDebug() {
        this.debugMode = false;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return {
            measurementId: this.measurementId,
            isInitialized: this.isInitialized,
            isAvailable: this.isAvailable(),
        };
    }
}
/**
 * Singleton instance export
 */
export const ga4Tracker = new GA4Tracker();
/**
 * Default export for convenience
 */
export default ga4Tracker;
